import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Download, Calendar, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function defaultRange() {
  const fim = new Date();
  const inicio = new Date(fim.getFullYear(), fim.getMonth(), 1);
  return { inicio: inicio.toISOString().slice(0, 10), fim: fim.toISOString().slice(0, 10) };
}

const PRESETS = [
  { label: 'Hoje', fn: () => { const d = new Date().toISOString().slice(0, 10); return { inicio: d, fim: d }; } },
  { label: 'Este Mês', fn: () => defaultRange() },
  { label: '30 dias', fn: () => { const f = new Date(); const i = new Date(); i.setDate(f.getDate() - 30); return { inicio: i.toISOString().slice(0, 10), fim: f.toISOString().slice(0, 10) }; } },
  { label: 'Este Ano', fn: () => { const y = new Date().getFullYear(); return { inicio: `${y}-01-01`, fim: `${y}-12-31` }; } },
];

export default function FinancasRelatorios() {
  const [range, setRange] = useState(defaultRange());
  const [loading, setLoading] = useState(null);

  const baixar = async (formato) => {
    setLoading(formato);
    try {
      const token = localStorage.getItem('financas_token');
      const url = `${BACKEND}/api/financas/relatorios/${formato}?inicio=${range.inicio}&fim=${range.fim}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Falha');
      const blob = await res.blob();
      const dl = document.createElement('a');
      dl.href = URL.createObjectURL(blob);
      dl.download = `relatorio_financas_${range.inicio}_${range.fim}.${formato}`;
      document.body.appendChild(dl);
      dl.click();
      dl.remove();
      toast.success(`Relatório ${formato.toUpperCase()} gerado`);
    } catch {
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="financas-relatorios-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3"><FileText className="w-6 h-6 md:w-7 md:h-7 text-emerald-400" /> Relatórios</h1>
        <p className="text-slate-400 text-xs md:text-sm mt-1">Exporte seus dados financeiros para PDF ou CSV</p>
      </div>

      <div className="bg-slate-900 border border-white/10 rounded-xl p-6 max-w-2xl">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-emerald-400" /> Período</h3>

        <div className="flex flex-wrap gap-2 mb-4">
          {PRESETS.map(p => (
            <Button key={p.label} variant="ghost" size="sm" onClick={() => setRange(p.fn())} className="bg-slate-800 hover:bg-slate-700 text-slate-300 border border-white/10">
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <div>
            <Label className="text-slate-400 text-xs">Início</Label>
            <Input data-testid="financas-relatorio-inicio" type="date" value={range.inicio} onChange={(e) => setRange({ ...range, inicio: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
          </div>
          <div>
            <Label className="text-slate-400 text-xs">Fim</Label>
            <Input data-testid="financas-relatorio-fim" type="date" value={range.fim} onChange={(e) => setRange({ ...range, fim: e.target.value })} className="bg-slate-950 border-white/10 text-white" />
          </div>
        </div>

        <div className="flex gap-3">
          <Button data-testid="financas-baixar-pdf" onClick={() => baixar('pdf')} disabled={loading === 'pdf'} className="flex-1 bg-red-600 hover:bg-red-500 text-white">
            <FileText className="w-4 h-4 mr-2" /> {loading === 'pdf' ? 'Gerando...' : 'Baixar PDF'}
          </Button>
          <Button data-testid="financas-baixar-csv" onClick={() => baixar('csv')} disabled={loading === 'csv'} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> {loading === 'csv' ? 'Gerando...' : 'Baixar CSV'}
          </Button>
        </div>

        <p className="text-slate-500 text-xs mt-4 flex items-center gap-2">
          <Download className="w-3.5 h-3.5" />
          O CSV pode ser aberto no Excel ou enviado ao seu contador.
        </p>
      </div>
    </div>
  );
}
